import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Send } from "lucide-react";

export default function Messages() {
  const { otherId } = useParams();
  const { user } = useAuth();
  const [threads, setThreads] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [otherName, setOtherName] = useState("");
  const endRef = useRef(null);

  const loadThreads = useCallback(
    () => api.get("/messages/threads").then((r) => setThreads(r.data)),
    [],
  );
  const loadThread = useCallback(() => {
    if (!otherId) return;
    api.get(`/messages/thread/${otherId}`).then((r) => setMsgs(r.data));
  }, [otherId]);

  useEffect(() => { loadThreads(); }, [loadThreads]);
  useEffect(() => {
    loadThread();
    // resolve counterpart name from threads list (avoids /handymen 404 for students)
    const t = threads.find((x) => x.other_id === otherId);
    if (t?.other_name) setOtherName(t.other_name);
  }, [otherId, threads, loadThread]);
  useEffect(() => {
    if (!otherId) return;
    const i = setInterval(loadThread, 4000);
    return () => clearInterval(i);
  }, [otherId, loadThread]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || !otherId) return;
    await api.post("/messages", { recipient_id: otherId, text: text.trim() });
    setText("");
    loadThread();
    loadThreads();
  };

  return (
    <div className="bg-cream min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display text-3xl text-charcoal mb-6">Messages</h1>
        <div className="grid lg:grid-cols-3 gap-6 bg-[#131C2E] rounded-2xl border border-[#1F2A40] overflow-hidden h-[70vh]">
          <aside className="lg:col-span-1 border-r border-[#1F2A40] overflow-y-auto">
            {threads.length === 0 && <p className="p-6 text-charcoal/60 text-sm">No conversations yet.</p>}
            {threads.map((t) => (
              <Link key={t.other_id} to={`/messages/${t.other_id}`} data-testid={`thread-${t.other_id}`}
                className={`block p-4 border-b border-[#1F2A40] hover:bg-beige transition-colors ${otherId === t.other_id ? "bg-beige" : ""}`}>
                <div className="flex items-center gap-3">
                  {t.other_picture ? (
                    <img src={t.other_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-terracotta/20 flex items-center justify-center text-terracotta font-medium">
                      {t.other_name?.[0]}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-charcoal truncate">{t.other_name}</p>
                    <p className="text-xs text-charcoal/60 truncate">{t.last_text}</p>
                  </div>
                </div>
              </Link>
            ))}
          </aside>
          <main className="lg:col-span-2 flex flex-col">
            {!otherId ? (
              <div className="flex-1 flex items-center justify-center text-charcoal/60">Select a conversation</div>
            ) : (
              <>
                <header className="p-4 border-b border-[#1F2A40]">
                  <p className="font-display text-lg text-charcoal">{otherName || "Conversation"}</p>
                </header>
                <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-cream/50">
                  {msgs.map((m) => (
                    <div key={m.message_id} className={`flex ${m.sender_id === user?.user_id ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[70%] rounded-2xl px-4 py-2 ${m.sender_id === user?.user_id ? "bg-terracotta text-white" : "bg-[#131C2E] border border-[#1F2A40]"}`}
                        data-testid={`msg-${m.message_id}`}>
                        <p className="text-sm">{m.text}</p>
                      </div>
                    </div>
                  ))}
                  <div ref={endRef} />
                </div>
                <form onSubmit={send} className="p-3 border-t border-[#1F2A40] flex gap-2">
                  <Input value={text} onChange={(e) => setText(e.target.value)}
                    placeholder="Type a message…" data-testid="msg-input" className="rounded-full h-11" />
                  <Button type="submit" data-testid="msg-send"
                    className="rounded-full bg-terracotta hover:bg-terracotta/90 text-white h-11 px-5">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
